import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, Search, CheckCircle2, Circle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { isLoggedIn, getRsvps, getRsvpSummary, setRsvpPaid, setRsvpCheckin, setGuestCheckin } from '../lib/api';
import { money, fmtLongDate } from '../lib/events';

const PAY = { door: 'At door', etransfer: 'E-transfer', online: 'Online' };
const FILTERS = [['all', 'All'], ['waiting', 'Not checked in'], ['in', 'Checked in'], ['unpaid', 'Unpaid']];
const owes = (r) => r.total > 0 && !r.paid;
const ticketsIn = (r) => (r.guests?.length ? (r.checked_in ? 1 : 0) + r.guests.filter((g) => g.checked_in).length : r.checked_in ? r.tickets || 1 : 0);
const allIn = (r) => r.checked_in && (r.guests || []).every((g) => g.checked_in);
const match = (r, f) => (f === 'waiting' ? !allIn(r) : f === 'in' ? ticketsIn(r) > 0 : f === 'unpaid' ? owes(r) : true);

function Stat({ label, value, tone = 'text-[#2f2f2f]', testid }) {
  return (
    <div className="bg-white rounded-sm border border-neutral-100 px-4 py-3" data-testid={testid}>
      <p className="font-sans-ah text-[11px] tracking-wide-ah text-[#999] uppercase">{label}</p>
      <p className={`font-serif-ah text-[26px] leading-tight ${tone}`}>{value}</p>
    </div>
  );
}

function GuestLine({ g, onCheck, busy }) {
  return (
    <div className="flex items-center gap-3 pl-4 border-l-2 border-neutral-200 py-1.5" data-testid={`checkin-guest-${g.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-sans-ah text-[15px] text-[#2f2f2f]">{g.name} <span className="text-[11px] tracking-wide-ah text-[#999] uppercase">Guest</span></p>
        {(g.phone || g.email) && <p className="font-sans-ah text-[12px] text-[#999] truncate">{[g.phone, g.email].filter(Boolean).join(' • ')}</p>}
      </div>
      <button onClick={() => onCheck(g)} disabled={busy} data-testid={`checkin-guest-toggle-${g.id}`}
        className={`min-w-[130px] font-sans-ah text-[12px] tracking-wide-ah px-3 py-2.5 rounded-sm flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors ${g.checked_in ? 'bg-green-600 text-white hover:bg-green-700' : 'border-2 border-[#1f2c33] text-[#1f2c33] hover:bg-[#1f2c33] hover:text-white'}`}>
        {g.checked_in ? <><CheckCircle2 size={15} /> CHECKED IN</> : <><Circle size={15} /> CHECK IN</>}
      </button>
    </div>
  );
}

function GuestCard({ r, onCheck, onPaid, onGuest, busy }) {
  return (
    <div data-testid={`checkin-row-${r.id}`} className={`bg-white rounded-sm border p-4 transition-colors ${allIn(r) ? 'border-green-500/40 bg-green-50/40' : 'border-neutral-100'}`}>
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex-1 min-w-[180px]">
        <p className="font-sans-ah text-[17px] text-[#2f2f2f] font-medium">{r.full_name}</p>
        {r.business_name && <p className="font-sans-ah text-[12px] text-[#4a9cd4]">Member: {r.business_name}</p>}
        <p className="font-sans-ah text-[13px] text-[#777]">
          {r.items?.length ? r.items.map((x) => `${x.qty}× ${x.name}`).join(', ') : `${r.tickets} ticket${r.tickets === 1 ? '' : 's'}`}
          {r.phone ? ` • ${r.phone}` : ''}
        </p>
      </div>
      {r.total > 0 && (
        <div className="text-right" data-testid={`checkin-payment-${r.id}`}>
          <p className="font-sans-ah text-[15px] text-[#2f2f2f]">{money(r.total)}</p>
          <p className={`font-sans-ah text-[11px] tracking-wide-ah uppercase ${r.paid ? 'text-green-600' : 'text-amber-600'}`}>{r.paid ? 'Paid' : `Owing • ${PAY[r.payment_method] || ''}`}</p>
        </div>
      )}
      <div className="flex gap-2 w-full sm:w-auto">
        {owes(r) && (
          <button onClick={() => onPaid(r)} disabled={busy} data-testid={`checkin-mark-paid-${r.id}`}
            className="flex-1 sm:flex-none font-sans-ah text-[13px] tracking-wide-ah px-4 py-3 rounded-sm border-2 border-amber-500 text-amber-700 hover:bg-amber-50 disabled:opacity-50">MARK PAID</button>
        )}
        <button onClick={() => onCheck(r)} disabled={busy} data-testid={`checkin-toggle-${r.id}`}
          className={`flex-1 sm:flex-none min-w-[150px] font-sans-ah text-[13px] tracking-wide-ah px-4 py-3 rounded-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors ${r.checked_in ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-[#1f2c33] text-white hover:bg-[#2c3e48]'}`}>
          {r.checked_in ? <><CheckCircle2 size={17} /> CHECKED IN</> : <><Circle size={17} /> CHECK IN</>}
        </button>
      </div>
    </div>
    {r.guests?.length > 0 && <div className="mt-3 space-y-1.5">{r.guests.map((g) => <GuestLine key={g.id} g={g} onCheck={(x) => onGuest(r, x)} busy={busy} />)}</div>}
    </div>
  );
}

function PrintSheet({ ev, rows }) {
  return (
    <div className="hidden print:block text-black" data-testid="checkin-print-sheet">
      <h1 className="text-[22px] font-bold">{ev?.title} — Door List</h1>
      <p className="text-[12px] mb-3">{fmtLongDate(ev?.date)} • {rows.length} RSVPs • {rows.reduce((a, r) => a + (r.tickets || 1), 0)} tickets</p>
      <table className="w-full text-[11px] border-collapse">
        <thead><tr>{['✓', 'Name', 'Member business', 'Tickets', 'Phone', 'Total', 'Paid'].map((h) => <th key={h} className="border border-black px-1.5 py-1 text-left">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <React.Fragment key={r.id}>
            <tr>
              <td className="border border-black px-1.5 py-1 w-6">{r.checked_in ? '✓' : ''}</td>
              <td className="border border-black px-1.5 py-1">{r.full_name}</td>
              <td className="border border-black px-1.5 py-1">{r.business_name || ''}</td>
              <td className="border border-black px-1.5 py-1">{r.items?.length ? r.items.map((x) => `${x.qty}× ${x.name}`).join(', ') : r.tickets}</td>
              <td className="border border-black px-1.5 py-1">{r.phone}</td>
              <td className="border border-black px-1.5 py-1">{r.total ? money(r.total) : '—'}</td>
              <td className="border border-black px-1.5 py-1">{!r.total ? '—' : r.paid ? 'Paid' : `☐ ${PAY[r.payment_method] || ''}`}</td>
            </tr>
            {(r.guests || []).map((g) => (
              <tr key={g.id}>
                <td className="border border-black px-1.5 py-1">{g.checked_in ? '✓' : ''}</td>
                <td className="border border-black px-1.5 py-1 pl-4">↳ {g.name} (guest of {r.full_name})</td>
                <td className="border border-black px-1.5 py-1" /><td className="border border-black px-1.5 py-1" />
                <td className="border border-black px-1.5 py-1">{g.phone}</td>
                <td className="border border-black px-1.5 py-1" /><td className="border border-black px-1.5 py-1" />
              </tr>
            ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CheckInPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(() => getRsvps(id).then((d) => setRows([...d].sort((a, b) => a.full_name.localeCompare(b.full_name)))).catch(() => setRows([])), [id]);
  useEffect(() => {
    if (!isLoggedIn()) { navigate('/admin'); return undefined; }
    getRsvpSummary().then((s) => setEv(s.find((x) => x.id === id) || null)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [id, load, navigate]);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (rows || []).filter((r) => match(r, filter) && (!term || `${r.full_name} ${r.business_name || ''} ${r.email} ${r.phone} ${(r.guests || []).map((g) => g.name).join(' ')}`.toLowerCase().includes(term)));
  }, [rows, q, filter]);
  const all = rows || [];
  const tickets = all.reduce((a, r) => a + (r.tickets || 1), 0);
  const inTickets = all.reduce((a, r) => a + ticketsIn(r), 0);
  const owing = all.filter(owes).reduce((a, r) => a + r.total, 0);

  const update = async (r, fn, patch, msg) => {
    setBusyId(r.id);
    try { await fn(); setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, ...patch } : x))); toast.success(msg); } catch { toast.error('Could not update, please try again'); }
    setBusyId('');
  };
  const onCheck = (r) => update(r, () => setRsvpCheckin(r.id, !r.checked_in), { checked_in: !r.checked_in }, r.checked_in ? `${r.full_name} un-checked` : `${r.full_name} checked in`);
  const onGuest = (r, g) => update(r, () => setGuestCheckin(r.id, g.id, !g.checked_in),
    { guests: r.guests.map((x) => (x.id === g.id ? { ...x, checked_in: !g.checked_in } : x)) }, g.checked_in ? `${g.name} un-checked` : `${g.name} checked in`);
  const onPaid = (r) => update(r, () => setRsvpPaid(r.id, true), { paid: true }, `${r.full_name} marked paid`);

  return (
    <div className="bg-[#f8f6f2] min-h-screen pt-28 pb-16 print:p-0 print:bg-white" data-testid="checkin-page">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 print:hidden">
        <Link to="/admin" className="inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah text-[#4a9cd4] mb-4"><ArrowLeft size={15} /> ADMIN</Link>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <p className="font-sans-ah text-[12px] tracking-wide-ah text-[#4a9cd4]">DOOR CHECK-IN</p>
            <h1 className="font-serif-ah text-[30px] sm:text-[38px] text-[#2f2f2f] leading-tight" data-testid="checkin-title">{ev?.title || 'Event'}</h1>
            {ev?.date && <p className="font-sans-ah text-[13px] text-[#888]">{fmtLongDate(ev.date)}</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={load} aria-label="Refresh" data-testid="checkin-refresh-btn" className="p-3 rounded-sm border border-neutral-300 text-[#555] hover:bg-white"><RefreshCw size={16} /></button>
            <button onClick={() => window.print()} data-testid="checkin-print-btn" className="font-sans-ah text-[12px] tracking-wide-ah px-4 py-3 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-2"><Printer size={16} /> PRINT LIST</button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <Stat label="In" value={`${inTickets}/${tickets}`} tone="text-green-700" testid="checkin-stat-in" />
          <Stat label="RSVPs" value={all.length} testid="checkin-stat-rsvps" />
          <Stat label="Owing" value={owing ? money(owing) : '$0'} tone={owing ? 'text-amber-600' : 'text-[#2f2f2f]'} testid="checkin-stat-owing" />
        </div>
        <div className="relative mb-3">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#aaa]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, business or phone" aria-label="Search guests" data-testid="checkin-search-input"
            className="w-full pl-11 pr-4 py-4 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[16px] focus:outline-none focus:border-[#4a9cd4]" />
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`checkin-filter-${k}`}
              className={`font-sans-ah text-[12px] px-3.5 py-2 rounded-full border transition-colors ${filter === k ? 'bg-[#1f2c33] border-[#1f2c33] text-white' : 'border-neutral-300 text-[#555] bg-white'}`}>
              {label} ({all.filter((r) => match(r, k)).length})
            </button>
          ))}
        </div>
        {rows === null ? <Loader2 size={26} className="animate-spin text-[#4a9cd4] mx-auto my-10" /> : (
          <div className="space-y-2.5">
            {shown.map((r) => <GuestCard key={r.id} r={r} onCheck={onCheck} onPaid={onPaid} onGuest={onGuest} busy={busyId === r.id} />)}
            {shown.length === 0 && <p className="font-sans-ah text-[14px] text-[#999] text-center py-10" data-testid="checkin-empty">{all.length ? 'No guests match.' : 'No RSVPs yet.'}</p>}
          </div>
        )}
      </div>
      <PrintSheet ev={ev} rows={all} />
    </div>
  );
}
